import type { DateTime } from "luxon";
import { formatPersonFullName, isNurseActiveInDepartment } from "../utils/formatters";
import type { Nurse, NurseInShift, ScheduleData } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";

export type DemoReplacement = {
	/** The shift the demo's caller-out leaves, and the schedule day and shift type it sits on. */
	shift: NurseInShift;
	date: DateTime;
	shiftType: string;
	/** Who takes it over. */
	replacement: Nurse;
};

/**
 * The Annie demo's "find someone to cover": `nurseId`'s shift on `date` in `schedule`, and who covers it: the person
 * Annie names when they're free that day, else the first active, free colleague in the same role.
 */
export const planDemoReplacement = (
	schedule: ScheduleData,
	nurseId: number,
	date: DateTime,
	replacementName: string,
	departmentId: string,
): DemoReplacement | undefined => {
	const isoDate = toIsoDate(date);
	const rowsOnDay = schedule.weeks.flatMap((week) =>
		week.weeklyShifts.flatMap((row) => row.days.filter((day) => toIsoDate(day.date) === isoDate).map((day) => ({ day, shiftType: row.type }))),
	);
	const working = new Set(rowsOnDay.flatMap(({ day }) => day.nurses.map((shift) => shift.nurseId)));
	const leaving = rowsOnDay.flatMap(({ day, shiftType }) =>
		day.nurses.filter((shift) => shift.nurseId === nurseId && !shift.isPendingToDelete).map((shift) => ({ shift, date: day.date, shiftType })),
	)[0];
	const originalNurse = schedule.nurses.find((nurse) => nurse.id === nurseId);
	if (!leaving || !originalNurse) return undefined;

	// Active in the chosen department (per-department source of truth), not the legacy global nurse.active — a
	// float-pool nurse inactive at a sibling facility is still eligible here.
	const candidates = schedule.nurses.filter(
		(nurse) => nurse.role === originalNurse.role && nurse.id !== nurseId && isNurseActiveInDepartment(nurse, departmentId) && !working.has(nurse.id),
	);
	// Annie names people first name first.
	const replacement = candidates.find((nurse) => formatPersonFullName(nurse, false) === replacementName) ?? candidates[0];
	return replacement && { ...leaving, replacement };
};
